<template>
  <div>
    <HomePageSkeleton v-if="loadingContent && isHomePage" />
    <CmsPageSkeleton v-if="loadingContent && !isHomePage" />
    <DynamicCmsPage
      v-if="canShowContent"
      :content="internalCmsContent"
      :current-page-slug="absoluteUrlSlug"
      :is-home-page="isHomePage"
    />
    <Pdp
      v-else-if="!isPageHidden && currentPage === ABSOLUTE_PAGE_URL.pdp"
      :key="absoluteUrlSlug"
      :slug="absoluteUrlSlug"
    />
    <Tracking v-else-if="isTrackingPage" />
    <SearchCategory
      v-else-if="!isPageHidden && currentPage === ABSOLUTE_PAGE_URL.categorySearch"
      :key="absoluteUrlSlug"
      :category-slug="absoluteUrlSlug"
    />
  </div>
</template>

<script setup lang='ts'>
import { sendRedirect } from 'h3';
import { PAGE_DELIVERY_KEYS } from '~~/constants/cms';
import { ABSOLUTE_PAGE_URL } from '~~/types/components/AbsoluteUrl/AbsoluteUrlPage';
import { isHomePageSlug } from '~~/helpers/routes/getPageInformation';

definePageMeta({
  layout: 'default-full-width',
  middleware: [
    'home-redirection',
    'check-search-params',
    'first-page-redirection',
    'typesense-sort-by-redirection',
  ],
});

const route = useRoute();
const router = useRouter();

const event = useRequestEvent();

const slugParam = computed(() => {

  const param = route.params.slug;

  if (!param) {

    return '';

  }

  return Array.isArray(param) ? param.join('/') : String(param);

});

const isPageHidden = useState<boolean>(`hidden-${slugParam.value}`, () => true);

const isHomePage = computed(() => isHomePageSlug(route.path));
const absoluteUrlSlug = computed(() => (isHomePage.value ? '' : slugParam.value));
const resolvedCmsSlug = computed(() => absoluteUrlSlug.value || PAGE_DELIVERY_KEYS.HOME.SLUG);

const {
  internalCmsContent,
  canShowContent,
  loadingContent,
  loadPage,
} = useInternalCms(resolvedCmsSlug.value);

const {
  currentPage,
} = useAbsoluteUrlPage(internalCmsContent);

const {
  makeNativeRouteRedirectionIfNeeded,
} = useNativeRoute(absoluteUrlSlug.value, internalCmsContent);

const ensureContentLoaded = async () => {

  isPageHidden.value = false;

  if (canShowContent.value) {

    return;

  }

  if (currentPage.value === ABSOLUTE_PAGE_URL.internalCms) {

    await loadPage();

  }

};

const isTrackingPage = computed(() => 'pp_tracking_id' in route.query);


await useLazyAsyncData(`root-${route.params.slug}`, async () => {

  const redirectTarget = makeNativeRouteRedirectionIfNeeded();

  if (redirectTarget) {

    const redirectPath = typeof redirectTarget.to === 'string'
      ? redirectTarget.to
      : router.resolve(redirectTarget.to).fullPath;

    if (event) {

      return sendRedirect(event, redirectPath, redirectTarget.redirectCode);

    }

    return navigateTo(redirectTarget.to, {
      redirectCode: redirectTarget.redirectCode,
    });

  }

  await ensureContentLoaded();

  return '';

});

</script>

<style lang="scss" scoped>
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;

.cms-slug {
  .content-block-wrapper {
    :deep(.category-items),
    :deep(.home-products),
    :deep(.marketing-card-container) {
      margin-left: auto;
      margin-right: auto;
      padding-right: 1.25rem;
      padding-left: 1.25rem;
      padding-top: 1rem;

      @include breakpoints.for-desktop {
        padding-right: 5rem;
        padding-left: 5rem;
      }
    }
  }
}
</style>
